import React, { useEffect, dayjs, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, List, Tag, Typography, Button, Space, Spin, Empty, Grid } from "antd";
import { ArrowRightOutlined, CalendarOutlined, EnvironmentOutlined } from "@ant-design/icons";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import API from "api/endpoints";
import axiosInstance from "api/axiosInstance";
import {
  JobApplicationStatusEnum,
  JobApplicationStatusLabel,
} from "Constants/Enums/JobApplicantStatusEnum";
import { JobPostingStatusEnum } from "Constants/Enums/JobPostingStatusEnum";
import { getPostingSkills } from "Common/skillHelpers";

const { Title, Text } = Typography;

function ApplicantPortal() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const candidateId = sessionStorage.getItem("candidateId");
  // The sidebar (and its 274px content offset) only appears from xl up. Below
  // that there is no sidebar, so the -274px counter-offset must NOT be applied
  // or it pushes the content off the left edge on tablet/mobile.
  const screens = Grid.useBreakpoint();

  useEffect(() => {
    if (candidateId) {
      fetchJobs();
    }
  }, [candidateId]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(API.candidate.getJobPostings(candidateId));
      setJobs(res.data || []);
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  // Helper to determine Tag color based on status
  const getStatusColor = (status) => {
    switch (status) {
      case JobApplicationStatusEnum.ShortList:
        return "success";
      case JobApplicationStatusEnum.Rejected:
        return "error";
      case JobApplicationStatusEnum.Pending:
        return "processing";
      default:
        return "default";
    }
  };

  // Per Mudassir (2026-05-20): pipeline status lives on `applicationStatus`,
  // not `status`. `status` is the JobPosting's Open/Closed flag.
  const resolveAppStatus = (job) =>
    job?.applicationStatus ??
    job?.jobApplicationStatus ??
    job?.jobApplicantStatus ??
    job?.currentStatus ??
    job?.pipelineStatus;

  const handleNavigate = (job) => {
  navigate("/shortlistEmployeeform", {
    state: {
      job: job,
      jobApplicantId: job.jobApplicantId,
    },
  });
};

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* DashboardLayout pushes content right by 274px at xl. Override the
          offset and center the content inside a max-width container so the
          applicant portal sits in the middle of the viewport. */}
      <div
        style={{
          marginLeft: screens.xl ? "-274px" : 0,
          width: screens.xl ? "100vw" : "100%",
          display: "flex",
          justifyContent: "center",
          padding: screens.md ? "24px 30px" : "16px 12px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ width: "100%", maxWidth: 1100 }}>
        <Title level={3} style={{ marginBottom: 24 }}>
          My Job Applications
        </Title>

        <Space style={{ marginBottom: 20 }}>
          <Button type="default" onClick={() => navigate("/viewcareer")}>
            Explore More Jobs
          </Button>
        </Space>

        {loading ? (
          <div style={{ textAlign: "center", marginTop: 50 }}>
            <Spin size="large" />
          </div>
        ) : (
          <List
            grid={{ gutter: 16, xs: 1, sm: 1, md: 1, lg: 2, xl: 2, xxl: 2 }}
            dataSource={jobs}
            locale={{ emptyText: <Empty description="No applications found" /> }}
            renderItem={(job) => {
              // Per Sara (May-25): mirror the internal MyApplications logic —
              // when the JobPosting is Filled and the applicant is still
              // mid-pipeline, show "Position Filled" so the candidate knows
              // their application is no longer active. Final states (Hired /
              // Reject) keep their own badge.
              const appStatus = resolveAppStatus(job);
              const isFinalState =
                appStatus === JobApplicationStatusEnum.Hired ||
                appStatus === JobApplicationStatusEnum.Reject;
              const isJobPostingFilled =
                job.status === JobPostingStatusEnum.Filled;
              const showFilledBadge = isJobPostingFilled && !isFinalState;
              return (
                <List.Item>
                  <Card
                    hoverable
                    onClick={() => handleNavigate(job)}
                    style={{
                      borderRadius: "12px",
                      borderLeft: showFilledBadge
                        ? "6px solid #94a3b8"
                        : "6px solid #52c41a",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div>
                        <Title level={4} style={{ margin: 0 }}>
                          {job.jobTitle}
                        </Title>
                        <Text type="secondary">{job.departmentName}</Text>
                      </div>
                      {showFilledBadge ? (
                        <Tag
                          color="default"
                          style={{
                            borderRadius: "10px",
                            padding: "0 12px",
                            background: "#f1f5f9",
                            color: "#475569",
                            borderColor: "#94a3b8",
                          }}
                        >
                          POSITION FILLED
                        </Tag>
                      ) : (
                        <Tag
                          color={getStatusColor(appStatus)}
                          style={{ borderRadius: "10px", padding: "0 12px" }}
                        >
                          {JobApplicationStatusLabel[appStatus]?.toUpperCase() ||
                            "SUBMITTED"}
                        </Tag>
                      )}
                    </div>

                    <Space direction="vertical" style={{ marginTop: 16, width: "100%" }}>
                      <Space>
                        <EnvironmentOutlined style={{ color: "#8c8c8c" }} />
                        <Text>
                          {job.jobLocationType} ({job.jobType})
                        </Text>
                      </Space>
                      <Space>
                        <CalendarOutlined style={{ color: "#8c8c8c" }} />
                        <Text size="small">
                          Applied on: {new Date(job.appliedOn).toLocaleDateString()}
                        </Text>
                      </Space>
                    </Space>

                    <div style={{ marginTop: 12 }}>
                      {getPostingSkills(job).slice(0, 3).map((skill) => (
                        <Tag key={skill} color="blue" style={{ fontSize: "11px" }}>
                          {skill}
                        </Tag>
                      ))}
                      {getPostingSkills(job).length > 3 && (
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          +{getPostingSkills(job).length - 3} more
                        </Text>
                      )}
                    </div>

                    {/* Position-filled hint — surfaces why no further actions
                        are available without forcing the candidate to open
                        the detail view first. */}
                    {showFilledBadge && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: "10px 14px",
                          background: "#f8fafc",
                          borderLeft: "3px solid #94a3b8",
                          borderRadius: 10,
                          fontSize: "0.82rem",
                          color: "#475569",
                          fontWeight: 500,
                        }}
                      >
                        🔒 This role has been filled. Your application is no
                        longer active.
                      </div>
                    )}
                  </Card>
                </List.Item>
              );
            }}
          />
        )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ApplicantPortal;
